import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { fetchCustomFields } from 'src/core/actions/customFields';
import { fetchCustomExports } from 'src/core/actions/exports';
import { fetchGroups } from 'src/core/actions/groups';
import { fetchUsers } from 'src/core/actions/users';
import withErrorBoundary, {
  ErrorBoundary,
} from 'src/core/common/components/withErrorBoundary';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { addNotification } from 'src/core/modules/app/notifications';
import { GraphQLProvider } from 'src/core/modules/bookkeep/components/GraphQLProvider';
import { type AppState } from 'src/core/reducers';
import {
  getIsFailedTransactionsEnabled,
  getIsFeatureEnabled,
  getIsSupervisionActive,
} from 'src/core/selectors/globalSelectors';
import { getUsers, isController } from 'src/core/selectors/users';

import { getLegacyFiltersFromUrl } from './components/list/PaymentFilters/types';
import { Payments } from './components/list/Payments';
import * as actions from './redux/actions';
import { getOpenedPayment, getOpenedPaymentInvoices } from './redux/selectors';
import { serialize } from './utils/convertFiltersForApi';
import { getExpenseCategoryCustomFieldId } from '../requests/redux/legacy/selectors';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Props = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type State = any;

class PaymentsAllContainer extends Component<Props, State> {
  filterParams: {
    [key: string]: string | string[];
  };

  static defaultProps = {
    payment: undefined,
    payments: undefined,
    invoices: [],
    paymentStats: undefined,
    selection: undefined,
    filters: undefined,
    pageInfo: undefined,
    bulkEditPayments: undefined,
    customFields: undefined,
    counters: undefined,
    teams: undefined,
    users: undefined,
    bulkActions: undefined,
  };

  constructor(props: Props) {
    super(props);

    this.filterParams = getLegacyFiltersFromUrl(props.location.search);
  }

  componentDidMount() {
    this.props.resetSelection();
    this.props.resetPayments();
    this.props.resetFailedTransactions();
    this.props.resetBulkEditPayments();

    this.props.fetchUsers({ withDelegators: true });
    this.props.fetchGroups();
    this.props.fetchCustomFields();

    if (this.props.isCustomExportsFeatureEnabled && this.props.isController) {
      this.props.fetchCustomExports();
    }

    // Immediately fetch payments, eventually using the URL params
    this.props.updateFilters(this.filterParams);

    if (
      this.props.match &&
      this.props.match.params.id &&
      this.props.match.params.state !== 'failed'
    ) {
      this.props.fetchPayment(
        this.props.match.params.id,
        (childPaymentId: string) => {
          this.props.history.push(
            routeFor(routes.PAYMENTS_ALL.path, {
              company: this.props.company.id,
              id: childPaymentId,
            }),
          );
        },
      );
      this.props.fetchPaymentInvoices(this.props.match.params.id);
      this.props.setPaymentsAllOpenedPayment(this.props.match.params.id);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.props.location.search !== nextProps.location.search) {
      this.filterParams = getLegacyFiltersFromUrl(nextProps.location.search);
      this.props.updateFilters(this.filterParams);
    }

    if (nextProps.filters && this.props.filters !== nextProps.filters) {
      this.props.resetPayments();
      this.fetchPayments({ filters: nextProps.filters });

      if (
        (!this.props.failedTransactions ||
          this.props.failedTransactions.length <= 0 ||
          !nextProps.failedTransactions ||
          nextProps.failedTransactions.length <= 0) &&
        !this.props.behavior?.loadingFailedPayments &&
        this.props.isFailedTransactionsEnabled
      ) {
        this.props.fetchAllFailedTransactions();
      }
    }

    if (nextProps.selection && this.props.selection !== nextProps.selection) {
      this.props.fetchPaymentsCounters({
        filters: serialize(this.props.filters),
        selection: nextProps.selection,
      });
    }
    if (
      nextProps.match &&
      nextProps.match.params.id &&
      nextProps.match.params.id !== this.props.match.params.id &&
      nextProps.match.params.state !== 'failed'
    ) {
      this.props.fetchPayment(nextProps.match.params.id);
      this.props.fetchPaymentInvoices(nextProps.match.params.id);
      this.props.setPaymentsAllOpenedPayment(nextProps.match.params.id);
    }
  }

  fetchPayments = (
    options: {
      first?: number;
      after?: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters?: Record<string, any>;
    } = {},
  ) => {
    this.props.fetchPayments({
      ...options,
      filters: serialize(options.filters || this.props.filters),
    });

    if (
      (!this.props.failedTransactions ||
        this.props.failedTransactions.length <= 0) &&
      this.props.isFailedTransactionsEnabled
    ) {
      this.props.fetchAllFailedTransactions();
    }
  };

  render() {
    const {
      users,
      payments,
      failedTransactions,
      payment,
      match,
      customFields,
      expenseCategoryCustomFieldId,
      selection,
      counters,
      teams,
      behavior,
      paymentStats,
      pageInfo,
      filters,
      bulkEditPayments,
      invoices,
      deleteDocumentaryEvidence,
      isFailedTransactionsEnabled,
    } = this.props;

    const paymentId = match.params.id;
    const paymentState = match.params.state;

    return (
      <ErrorBoundary
        context={{ scope: 'payments-all-grapes', team: 'finance-accountant' }}
      >
        <Payments
          itemId={paymentId}
          itemState={paymentState}
          isLoading={
            behavior.loader ||
            (behavior.loadingFailedPayments && isFailedTransactionsEnabled)
          }
          payments={payments}
          failedTransactions={failedTransactions}
          bulkEditPayments={bulkEditPayments}
          users={users}
          payment={payment}
          invoices={invoices}
          teams={teams}
          customFields={customFields}
          pageInfo={pageInfo}
          expenseCategoryCustomFieldId={expenseCategoryCustomFieldId}
          filters={filters}
          updatePaymentLocally={this.props.updatePaymentLocally}
          fetchPayment={this.props.fetchPayment}
          deleteInvoice={this.props.deleteInvoice}
          fetchPaymentInvoices={this.props.fetchPaymentInvoices}
          incrementPaymentInvoices={this.props.incrementPaymentInvoices}
          deleteDocumentaryEvidence={deleteDocumentaryEvidence}
          paymentStats={paymentStats}
          fetchPayments={this.fetchPayments}
          updateFilters={this.props.updateFilters}
          selection={selection}
          updateSelection={this.props.updateSelection}
          counters={counters}
          bulkEdit={this.props.bulkEdit}
          download={this.props.download}
          remindInvoices={this.props.remindInvoices}
          bulkActions={this.props.bulkActions}
          bulkMarkAsMissing={this.props.bulkMarkAsMissing}
          isSupervisionActive={this.props.isSupervisionActive}
          hidePanel={this.props.hidePanel}
        />
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = (state: AppState) => {
  const {
    global: { company, user },
    payments,
  } = state;

  return {
    isCustomExportsFeatureEnabled: getIsFeatureEnabled(
      state,
      FEATURES.CUSTOM_EXPORTS,
    ),
    user,
    isController: isController(state),
    company,
    users: getUsers(state),
    payment: getOpenedPayment(state),
    invoices: getOpenedPaymentInvoices(state),
    expenseCategoryCustomFieldId: getExpenseCategoryCustomFieldId(state),
    failedTransactions: payments.failedTransactions,
    payments: payments.allPayments,
    paymentStats: payments.allPaymentStats,
    selection: payments.allPaymentsSelection,
    filters: payments.filters,
    pageInfo: payments.allPaymentsPageInfo,
    teams: payments.groups,
    customFields: payments.customFields,
    counters: payments.allPaymentsCounters,
    behavior: payments.behavior,
    bulkEditPayments: payments.bulkEditPayments,
    bulkActions: payments.allPaymentsBulkActions,
    errors: payments.errors,
    isSupervisionActive: getIsSupervisionActive(state),
    lastLoadingPaymentId: payments.lastLoadingPaymentId,
    isFailedTransactionsEnabled: getIsFailedTransactionsEnabled(state),
  };
};

const mapDispatchToProps = {
  fetchPayment: actions.fetchPayment,
  setPaymentsAllOpenedPayment: actions.setPaymentsAllOpenedPayment,
  fetchPayments: actions.fetchAllPayments,
  fetchAllFailedTransactions: actions.fetchAllFailedTransactions,
  fetchPaymentInvoices: actions.fetchPaymentInvoices,
  fetchPaymentsCounters: actions.fetchAllPaymentsCounters,
  resetPayments: actions.resetAllPayments,
  resetFailedTransactions: actions.resetAllFailedTransactions,
  resetSelection: actions.resetAllPaymentsSelection,
  updateFilters: actions.updateFilters,
  updateSelection: actions.updateAllPaymentsSelection,
  remindInvoices: actions.remindInvoices,
  download: actions.downloadPayments,
  bulkEdit: actions.bulkEditPayments,
  bulkMarkAsMissing: actions.bulkMarkAsMissing,
  updatePaymentLocally: actions.updatePaymentLocally,
  hidePanel: actions.hidePaymentPanel,
  resetBulkEditPayments: actions.resetBulkEditPayments,
  deleteInvoice: actions.deleteInvoice,
  fetchCustomFields,
  fetchGroups,
  fetchUsers,
  fetchCustomExports,
  pushNotif: addNotification,
  incrementPaymentInvoices: actions.incrementPaymentInvoices,
  deleteDocumentaryEvidence: actions.deleteDocumentaryEvidence,
};

const withGraphQLProvider = (
  Comp: ReturnType<ReturnType<typeof withErrorBoundary>>,
) => {
  const PaymentContainerWithGraphQLProvider = (props: Props) => {
    return (
      <GraphQLProvider>
        <Comp {...props} />
      </GraphQLProvider>
    );
  };
  PaymentContainerWithGraphQLProvider.displayName =
    'PaymentContainerWithGraphQLProvider';
  return PaymentContainerWithGraphQLProvider;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  withRouter(
    withGraphQLProvider(
      withErrorBoundary({
        scope: 'payments-all',
        team: 'finance-accountant',
      })(PaymentsAllContainer),
    ),
  ),
);
